import * as d3 from 'd3';
import './d3-pricechart.css';
import { colors, LEGEND_ITEM_RADIUS } from '../utils';

export default function () {
  const margin = { top: 30, right: 20, bottom: 60, left: 80 };

  let width = 400;
  let height = 300;
  let svg;
  let _data;
  let yExtent;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let selected;
  let options;
  const itemsMap = {
    Price: ['Price Fixed', 'Price Real'],
    Delta: ['PV01', 'IE01'],
    Sensitivity: ['Inflation Sensitivity'],
  }

  const dispatch = d3.dispatch('selected');

  function chart(selection) {
    selection.each(function (data) {
      _data = data.data;
      yExtent = data.yExtent;
      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();

        g.append('g')
          .attr('class', 'price-chart__price');

        g.append('g')
          .attr('class', 'price-chart__delta');

        g.append('g')
          .attr('class', 'price-chart__sensitivity');

        createLegend();
        createLabels();
      }

      svg.attr('width', width).attr('height', height);

      drawAxes();

      switch (selected) {
        case ('Price'):
          drawPrice();
          svg.select('.price-chart__price').style('display', 'block');
          svg.select('.price-chart__delta').style('display', 'none');
          svg.select('.price-chart__sensitivity').style('display', 'none');
          break;
        case ('Delta'):
          drawDelta();
          svg.select('.price-chart__price').style('display', 'none');
          svg.select('.price-chart__delta').style('display', 'block');
          svg.select('.price-chart__sensitivity').style('display', 'none');
          break;
        case ('Sensitivity'):
          drawSensitivity();
          svg.select('.price-chart__price').style('display', 'none');
          svg.select('.price-chart__delta').style('display', 'none');
          svg.select('.price-chart__sensitivity').style('display', 'block');
          break;
        default:
          console.log('Unrecognised price chart selection: ' + selected);
      }

      drawLegend();
      drawLabels();
    });
  }

  function createScales() {
    const paddingFraction = 0.1;
    x = d3.scaleLinear().rangeRound([0, chartWidth]);
    y = d3.scaleLinear().rangeRound([chartHeight, 0]);

    const xExtent = d3.extent(_data[0].map(function (d) { return d.term; }));

    if (selected === 'Sensitivity') {
      yExtent = [0, 1];
    }

    const yWidth = yExtent[1] - yExtent[0];

    x.domain(xExtent);
    y.domain([yExtent[0], yExtent[1] + (selected === 'Sensitivity' ? 0 : (2 * paddingFraction * yWidth))]);
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'price-chart__axis price-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'price-chart__axis price-chart__axis--y')
  }

  function createLegend() {
    svg.select('g')
      .append('g')
      .attr('class', 'price-chart__legend')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'end');
  }

  function createLabels() {
    svg.select('g')
      .append('text')
      .attr('class', 'price-chart__label price-chart__label--x')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Term (years)');

    svg.select('g')
      .append('text')
      .attr('class', 'price-chart__label price-chart__label--y')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Value');
  }

  function drawAxes() {
    const xAxis = svg.select('.price-chart__axis--x');

    xAxis.attr('transform', 'translate(0,' + chartHeight + ')')
      .call(d3.axisBottom(x).tickSize(-chartHeight))
      .select('.domain').remove();

    xAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    xAxis.selectAll('text')
      .attr('dx', '-0.8em')
      .attr('dy', '.35em')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');

    const yAxis = svg.select('.price-chart__axis--y');

    yAxis.call(d3.axisLeft(y).tickFormat(selected === 'Sensitivity' ? d3.format('.0%') : d3.format(',.2r')).tickSize(-chartWidth))
      .select('.domain').remove();

    yAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    yAxis.selectAll('.tick text')
      .attr('x', -6)
      .attr('dy', 4);
  }

  function drawPrice() {
    const line = d3.line()
      .curve(d3.curveLinear)
      .x(function (d, ) { return x(d.term); })
      .y(function (d) { return y(d.value); });

    const curves = svg.select('.price-chart__price')
      .selectAll('path')
      .data(_data, function (d, i) { return itemsMap[selected][i]; });

    curves.exit()
      .remove();

    const curvesEnter = curves.enter()
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', function (d, i) { return [colors.blue, colors.green][i]; })
      .attr('stroke-width', 2)
      .attr('d', line);

    curves.merge(curvesEnter)
      .attr('d', line);
  }

  function drawDelta() {
    // NOTE: We subtract IE01 from PV01 to get a difference
    const newData = _data[0].map(function (d, i) { return { term: d.term, ie01: d.value, pv01: _data[1][i].value - d.value }; });

    const stack = d3.stack()
      .keys(['ie01', 'pv01'])
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const series = stack(newData);

    const area = d3.area()
      .curve(d3.curveStepBefore)
      .x(function (d) { return x(d.data.term); })
      .y1(function (d) { return y(d[0]); })
      .y0(function (d) { return y(d[1]); });

    const areas = svg.select('.price-chart__delta')
      .selectAll('.price-chart__delta--area')
      .data(series, function (d, i) { return itemsMap[selected][i]; });

    areas.exit()
      .remove();

    const areasEnter = areas.enter().append('path')
      .attr('class', 'price-chart__delta--area')
      .attr('fill', function (d, i) { return [colors.blue, colors.green][i]; })
      .attr('opacity', 1);

    areas.merge(areasEnter)
      .attr('d', area);
  }

  function drawSensitivity() {
    const line = d3.line()
      .curve(d3.curveLinear)
      .x(function (d, i) { return x(d.term); })
      .y(function (d) { return y(d.value); });

    const area = d3.area()
      .curve(d3.curveLinear)
      .x(function (d) { return x(d.term); })
      .y1(function (d) { return y(d.value); })
      .y0(function (d) { return y(0); });

    const curves = svg.select('.price-chart__sensitivity')
      .selectAll('.price-chart__sensitivity--line')
      .data(_data, function (d, i) { return itemsMap[selected][i]; });

    curves.exit()
      .remove();

    const curvesEnter = curves.enter().append('path')
      .attr('class', 'price-chart__sensitivity--line')
      .attr('fill', 'none')
      .attr('stroke', 'rgb(31, 119, 180)')
      .attr('stroke-width', 2);

    curves.merge(curvesEnter)
      .attr('d', line);

    const areas = svg.select('.price-chart__sensitivity')
      .selectAll('.price-chart__sensitivity--area')
      .data(_data, function (d, i) { return itemsMap[selected][i]; });

    areas.exit()
      .remove();

    const areasEnter = areas.enter().append('path')
      .attr('class', 'price-chart__sensitivity--area')
      .attr('fill', colors.blue)
      .attr('opacity', 0.5);

    areas.merge(areasEnter)
      .attr('d', area);
  }

  function drawLegend() {
    const data = itemsMap[selected];

    const legend = svg.select('.price-chart__legend')
      .selectAll('g')
      .data(data);

    legend.exit().remove();

    const legendEnter = legend.enter().append('g');

    legend.merge(legendEnter)
      .attr('transform', function (d, i) { return 'translate(' + (i * -80 + chartWidth + 4) + ',' + -24 + ')'; });

    legendEnter.append('circle')
      .attr('cy', 9)
      .attr('r', LEGEND_ITEM_RADIUS)
      .attr('stroke', 'black')
      .attr('stroke-width', 1);

    legendEnter.append('text')
      .attr('y', 9.5)
      .attr('dy', '0.32em')
      .attr('fill', 'black');

    legend.merge(legendEnter)
      .select('circle').attr('cx', -10)
      .attr('fill', function (d, i) { return selected === 'Sensitivity' ? colors.blue : [colors.green, colors.blue][i]; });

    legend.merge(legendEnter)
      .select('text')
      .attr('x', -21)
      .text(function (d) { return d; });
  }

  function drawLabels() {
    svg.select('.price-chart__label--x')
      .attr('x', chartWidth / 2)
      .attr('y', chartHeight + margin.top + 15);

    svg.select('.price-chart__label--y')
      .attr('transform', 'translate(-' + margin.left + ', ' + chartHeight / 2 + ')rotate(-90)');
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.selected = function (value) {
    if (!arguments.length) return selected;
    selected = value;
    return chart;
  }

  chart.options = function (value) {
    if (!arguments.length) return options;
    options = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-pricechart.js